import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import BulkFollowListModal from 'js/social/shared/big_cards/BulkFollowListModal';
import { followableUserToSuggestion } from 'js/social/shared/utils';
import { SnackbarContext } from 'js/social/contexts/snackbar';
import * as ApiUtils from 'js/utils/api_utils';
import { FollowableUser } from '../types';
import { currentUser } from 'js/utils/user';

const SnackbarContent = styled.div`
  display: flex;
  gap: 4px;
`;

const SnackbarLink = styled.a`
  text-decoration: underline;
  font-size: inherit;
  color: inherit;
`;

type Props = {
  viewedUserDisplayName: string;
  userId: number;
};

export const ProfileBulkFollowModal: React.FC<Props> = ({
  viewedUserDisplayName,
  userId,
}) => {
  const { showSnackbar } = useContext(SnackbarContext);
  const [showPostFollow, setShowPostFollow] = useState(false);
  const [followSuggestions, setFollowSuggestions] = useState<FollowableUser[]>(
    [],
  );
  const profileFollowButtonRef = useRef<Element | null>(null);

  const handleProfileFollow = useCallback(() => {
    if (profileFollowButtonRef.current?.textContent === 'Follow') {
      setShowPostFollow(
        !!currentUser && followSuggestions && followSuggestions.length > 0,
      );
    }
  }, [followSuggestions]);

  const handleCloseModal = useCallback(
    (followed: boolean) => {
      setShowPostFollow(false);
      if (followed) {
        showSnackbar(
          <SnackbarContent>
            <SnackbarLink href={routes.home_url()}>View Updates</SnackbarLink>
            from academics you're now following
          </SnackbarContent>,
          'success',
          () => (location.href = routes.home_url()),
          5000,
        );
      }
    },
    [showSnackbar],
  );

  useEffect(() => {
    ApiUtils.get(
      routes.api_v0_community_profile_follow_suggestion_path({
        user_id: userId,
        subdomain_param: 'api',
      }),
    ).then((res) => {
      setFollowSuggestions(res.users as FollowableUser[]);
    });
  }, [userId]);

  useEffect(() => {
    if (followSuggestions && followSuggestions.length > 0) {
      // Also a backbone button - we want to display a modal when the user
      // clicks the follow button to follow another user.
      const button = document.getElementsByClassName(
        'js-profile-follow-button',
      )[0];
      if (button) {
        // Remove previous popover/modal
        button.classList.remove('js-profile-follow-button');

        profileFollowButtonRef.current = button;
        button.addEventListener('click', handleProfileFollow);
        return () => button.removeEventListener('click', handleProfileFollow);
      }
    }
  }, [handleProfileFollow, followSuggestions]);

  if (showPostFollow) {
    return (
      <BulkFollowListModal
        open
        initialFollowSuggestions={followSuggestions.map(
          followableUserToSuggestion,
        )}
        cardType="profile_similar_people"
        topic={{ name: viewedUserDisplayName }}
        onClose={handleCloseModal}
      />
    );
  }
  return null;
};
